import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { BANDEIRA_IDIOMA, NOME_IDIOMA, useIdiomaSite } from '../../../utils/idiomaSite';
import IdiomasMenu from './IdiomasMenu';
import styles from './IdiomasMenu.module.css';

// ITEM "IDIOMA" NA BARRA DO COMPUTADOR (22/10/2026) — para os modelos cujo menu, no computador, é a
// própria barra (sem ☰: topos 5, 6, 7, 8, 10, 11, 13, 15 e 17). Botão com a bandeira + sigla do idioma
// ativo que abre um menu suspenso com as 6 opções (IdiomasMenu). Some na largura em que o modelo passa a
// usar o ☰ (`ocultarAte` = o breakpoint do hambúrguer do modelo): ali a linha "Idioma" está na gaveta.
// O visual é do modelo: classes do item da barra e do menu suspenso do próprio modelo quando ele tem;
// senão, um suspenso branco discreto que herda a fonte — este vai num PORTAL no <body>, posicionado pelo
// botão (fixed), porque há barras com `overflow: hidden` (ex.: Panorama) que cortariam o suspenso.
//
// RODADA 2 (22/10/2026) — o seletor passa a morar NA BARRA DO TOPO também no celular (sai a linha da
// gaveta). Props opcionais novas, compatíveis com quem já usa (TopoNorte): `compacto` / `compactoAte`
// (só bandeira + ▾ — sempre, ou até a largura do celular do modelo) e `ocultarAcima` (instância que só
// existe no celular, quando o lugar do item no celular é outro). O suspenso padrão fica sempre DENTRO
// da tela (vira para o outro lado quando não cabe).

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');
// O portal não herda o estilo da barra: leva só as variáveis --idm-* (cor/margem do botão ficam lá).
const soVariaveis = (estilo?: CSSProperties): CSSProperties =>
    Object.fromEntries(Object.entries(estilo || {}).filter(([k]) => k.startsWith('--'))) as CSSProperties;

const OCULTAR_ATE = {
    640: styles.ate640,
    767: styles.ate767,
    768: styles.ate768,
    850: styles.ate850,
    900: styles.ate900,
    960: styles.ate960,
    992: styles.ate992,
    1023: styles.ate1023,
    1024: styles.ate1024,
    1080: styles.ate1080,
    1279: styles.ate1279,
} as const;
// some ACIMA da largura (instância só do celular) e vira compacta ATÉ a largura
const OCULTAR_ACIMA = {
    600: styles.acima600,
    640: styles.acima640,
    767: styles.acima767,
    768: styles.acima768,
    850: styles.acima850,
    900: styles.acima900,
    960: styles.acima960,
    992: styles.acima992,
    1023: styles.acima1023,
    1024: styles.acima1024,
    1080: styles.acima1080,
    1279: styles.acima1279,
} as const;
const COMPACTO_ATE = {
    600: styles.compacto600,
    640: styles.compacto640,
    767: styles.compacto767,
    768: styles.compacto768,
    850: styles.compacto850,
    900: styles.compacto900,
    960: styles.compacto960,
    992: styles.compacto992,
    1023: styles.compacto1023,
    1024: styles.compacto1024,
    1080: styles.compacto1080,
    1279: styles.compacto1279,
} as const;
const LARGURA_SUSPENSO = 216; // .painel: min-width 200 + folga

type Props = {
    /** Largura (px) até a qual o item some — a mesma do ☰ do modelo. */
    ocultarAte?: keyof typeof OCULTAR_ATE;
    classeWrapper?: string;
    classeBotao?: string;
    classeBotaoAberto?: string;
    /** Menu suspenso do modelo (senão, o padrão) e, se o modelo separa, a caixa dentro dele. */
    classePainel?: string;
    classeCaixa?: string;
    classeOpcao?: string;
    classeOpcaoAtiva?: string;
    /** Abre alinhado à direita do botão (padrão) ou à esquerda. */
    alinhar?: 'direita' | 'esquerda';
    estilo?: CSSProperties;
    /** Miolo do botão no jeito do modelo: classe do ícone (bandeira) e do texto, e o texto (sigla "PT" ou
     *  nome "Português"). Padrão: bandeira + sigla + seta. */
    classeIcone?: string;
    classeTexto?: string;
    rotulo?: 'sigla' | 'nome';
    semSeta?: boolean;
    /** Só bandeira + ▾ (sem a sigla), sempre — para o celular / barras apertadas. */
    compacto?: boolean;
    /** Só bandeira + ▾ até esta largura (px); acima dela, o botão normal. */
    compactoAte?: keyof typeof COMPACTO_ATE;
    /** Some acima desta largura (px): instância só do celular, quando o lugar no celular é outro. */
    ocultarAcima?: keyof typeof OCULTAR_ACIMA;
    /** Botão padrão em pílula com contorno na cor do texto (modelos cujas ações da barra são pílulas). */
    pilula?: boolean;
};

const IdiomaNavItem: React.FC<Props> = ({
    ocultarAte,
    classeWrapper,
    classeBotao,
    classeBotaoAberto,
    classePainel,
    classeCaixa,
    classeOpcao,
    classeOpcaoAtiva,
    alinhar = 'direita',
    estilo,
    classeIcone,
    classeTexto,
    rotulo = 'sigla',
    semSeta = false,
    compacto = false,
    compactoAte,
    ocultarAcima,
    pilula = false,
}) => {
    const atual = useIdiomaSite();
    const [aberto, setAberto] = useState(false);
    const raiz = useRef<HTMLDivElement>(null);
    const botao = useRef<HTMLButtonElement>(null);
    const painel = useRef<HTMLDivElement>(null);
    const idPainel = `idm-nav-${useId().replace(/:/g, '')}`;
    const noPortal = !classePainel;
    const [posicao, setPosicao] = useState<CSSProperties | null>(null);

    // suspenso padrão (portal): posição fixa a partir do botão; acompanha rolagem e redimensionamento
    useEffect(() => {
        if (!aberto || !noPortal) return;
        const medir = () => {
            const r = botao.current?.getBoundingClientRect();
            if (!r) return;
            // sempre dentro da tela: vira para o outro lado quando o suspenso não cabe (botão perto da borda)
            const lado =
                alinhar === 'esquerda'
                    ? r.left + LARGURA_SUSPENSO > window.innerWidth - 8 ? 'direita' : 'esquerda'
                    : r.right - LARGURA_SUSPENSO < 8 ? 'esquerda' : 'direita';
            setPosicao(
                lado === 'esquerda'
                    ? { position: 'fixed', top: r.bottom + 8, left: Math.max(8, r.left), right: 'auto' }
                    : { position: 'fixed', top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right), left: 'auto' }
            );
        };
        medir();
        window.addEventListener('scroll', medir, { passive: true });
        window.addEventListener('resize', medir);
        return () => {
            window.removeEventListener('scroll', medir);
            window.removeEventListener('resize', medir);
        };
    }, [aberto, noPortal, alinhar]);

    // fecha no clique fora e no Esc (devolvendo o foco ao botão)
    useEffect(() => {
        if (!aberto) return;
        const fora = (e: PointerEvent) => {
            const alvo = e.target as Node;
            if (!raiz.current?.contains(alvo) && !painel.current?.contains(alvo)) setAberto(false);
        };
        const tecla = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            setAberto(false);
            botao.current?.focus();
        };
        document.addEventListener('pointerdown', fora);
        document.addEventListener('keydown', tecla);
        return () => {
            document.removeEventListener('pointerdown', fora);
            document.removeEventListener('keydown', tecla);
        };
    }, [aberto]);

    const lista = (
        <IdiomasMenu
            titulo={null}
            vertical
            classeBloco={classeCaixa}
            classeOpcao={classeOpcao ?? (noPortal ? styles.opcaoSuspensa : undefined)}
            classeOpcaoAtiva={classeOpcaoAtiva ?? (noPortal ? styles.opcaoSuspensaAtiva : undefined)}
            aoEscolher={() => setAberto(false)}
        />
    );

    return (
        <div
            ref={raiz}
            className={cx(
                styles.navItem,
                ocultarAte && OCULTAR_ATE[ocultarAte],
                ocultarAcima && OCULTAR_ACIMA[ocultarAcima],
                compacto && styles.compacto,
                compactoAte && COMPACTO_ATE[compactoAte],
                classeWrapper
            )}
            style={estilo}
            data-idioma-nav=""
        >
            <button
                ref={botao}
                type="button"
                className={cx(classeBotao ? cx(styles.navBotaoModelo, classeBotao) : cx(styles.navBotao, pilula && styles.navPilula), aberto && classeBotaoAberto)}
                aria-haspopup="true"
                aria-expanded={aberto}
                aria-controls={idPainel}
                aria-label="Idioma"
                title="Idioma"
                onClick={() => setAberto((v) => !v)}
            >
                <span className={classeIcone ?? styles.navIcone}>
                    {/* eslint-disable-next-line @next/next/no-img-element -- bandeira SVG de 20px do CDN do widget */}
                    <img className={styles.bandeira} src={BANDEIRA_IDIOMA[atual]} alt="" width={20} height={20} decoding="async" />
                </span>
                <span className={cx('notranslate', styles.textoNav, classeTexto ?? styles.sigla)} translate="no">
                    {rotulo === 'nome' ? NOME_IDIOMA[atual] : atual.toUpperCase()}
                </span>
                {semSeta ? null : (
                    <svg className={cx(styles.seta, aberto && styles.setaAberta)} viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
                        <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </button>
            {aberto && !noPortal ? (
                <div ref={painel} id={idPainel} className={classePainel}>
                    {lista}
                </div>
            ) : null}
            {aberto && noPortal && posicao
                ? createPortal(
                      <div ref={painel} id={idPainel} className={styles.painel} style={{ ...soVariaveis(estilo), ...posicao }} data-idioma-nav-painel="">
                          {lista}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default IdiomaNavItem;
